import { defaultRegion } from "@p0security/integrations-aws-shared/constants";
import { awsPartitionFromUse } from "@p0security/integrations-aws-shared/gov-cloud";
import { existingIamWriteAndAssessmentItems } from "@p0security/integrations-aws-shared/install-options";
import {
  inventoryInstallCommands,
  inventoryTerraform,
} from "@p0security/integrations-aws-shared/policy/resource-inventory";
import { installedItems } from "@p0security/shared/install/installed";
import { Typography } from "@p0security/ui";

import type { AwsInstaller } from "./types";

const resourceExplorerCommands = {
  enableAllRegions: () => `for region in $( \\
    aws account list-regions --output text \\
    --query 'Regions[?(RegionOptStatus!=\`DISABLED\` && RegionOptStatus!=\`DISABLING\`)].RegionName' \\
); do 
 state=$(aws resource-explorer-2 get-index --region $region --query 'State' --output text) 
 if [[ $? != 0 ]]; then
   echo "Creating index in $region"
   aws resource-explorer-2 create-index --region $region
   echo "Index created in $region"
 else
   if [[ $\{state} == "DELETED" ]]; then
      echo "Creating index in $region"
      aws resource-explorer-2 create-index --region $region
      echo "Index created in $region"
   else
      echo "Index already exists in $region"
   fi
 fi 
done 
`,
  promoteAggregator: (
    region: string
  ) => `export AGGREGATOR_ARN=$(aws resource-explorer-2 list-indexes --query 'Indexes[?Type==\`AGGREGATOR\`].Arn' --output text)  
  if [[ -z $AGGREGATOR_ARN ]]; then
     state=$(aws resource-explorer-2 get-index --region ${region} --query 'State' --output text)
     if [[ $? != 0 ]]; then
        echo "make sure ${region} index is created"
        aws resource-explorer-2 create-index --region ${region}
        state=$(aws resource-explorer-2 get-index --region ${region} --query 'State' --output text)
     fi
     while [[ $state == "CREATING" ]]; do
        echo "Waiting for ${region} index to be created"
        sleep 5
        state=$(aws resource-explorer-2 get-index --region ${region} --query 'State' --output text)
     done
     arn=$(aws resource-explorer-2 get-index --region ${region} --query 'Arn' --output text)
     aws resource-explorer-2 update-index-type --arn $arn --type AGGREGATOR --region ${region}
     if [[ $? != 0 ]]; then
        echo "Failed to promote index to aggregator, if there is Service Quota Exception, please try after 24 hours or request a quota increase"
     else
        echo "Index promoted to aggregator"
     fi
  else
    echo "Aggregator index already exists"
  fi
`,
  createDefaultView:
    () => `export AGGREGATOR_REGION=$(aws resource-explorer-2 list-indexes \\
    --query 'Indexes[?Type==\`AGGREGATOR\`].Region' \\
    --output text \\
)
if [[ $AGGREGATOR_REGION == "None" ]]; then
  echo "Aggregator index does not exist, please create aggregator index"
else
  DEFAULT_VIEW=$(aws resource-explorer-2 get-default-view --region $AGGREGATOR_REGION --query 'ViewArn' --output text)
  if [[ $DEFAULT_VIEW != "None" ]]; then
    echo "Default view already exists"
  else
    echo "Creating default view"
    ARN=$(aws resource-explorer-2 create-view  --view-name all-resources-p0 --included-properties Name=tags --region $AGGREGATOR_REGION --query "View.ViewArn" --output text)        
    if [[ $? != 0 ]]; then
        echo "Failed to create default view"
        echo "Checking if view already exists in aggregator region"
        ARN=$(aws resource-explorer-2 list-views --query 'Views[?contains(@, \`/all-resources-p0/\`)]' --region $AGGREGATOR_REGION --output text)
    else
        echo "created $ARN"
        ARN=$(aws resource-explorer-2 get-view --view-arn $ARN --region $AGGREGATOR_REGION --query "View.ViewArn" --output text)
    fi
    if [[ $? != 0 ]]; then
        echo "Failed to create default view"
    else
        aws resource-explorer-2 associate-default-view --view-arn $ARN --region $AGGREGATOR_REGION
        if [[ $? != 0 ]]; then
            echo "Failed to associate default view"
        else
            echo "Default view associated for region"
        fi
    fi
    
  fi       
fi
`,
};

const inventoryInstructions: AwsInstaller["components"]["inventory"]["instructions"] =
  (context, id) => {
    const gcloudServiceAccountId = context.config.base[id]?.serviceAccountId;
    const awsPartition = awsPartitionFromUse(context.config, id, "inventory");
    const aggregatorIndexRegion = defaultRegion(awsPartition);
    const signinHost =
      awsPartition === "aws-us-gov"
        ? "signin.amazonaws-us-gov.com"
        : "signin.aws.amazon.com";
    const signinUrl = `https://${id}.${signinHost}/console`;
    return {
      help: (
        <>
          <Typography.Paragraph>
            P0 Security relies on the Resource Explorer API to list resources in
            AWS. The following script:
            <ul>
              <li>
                Enables Resource Explorer in those AWS regions where it is not
                already enabled
              </li>
              <li>
                Promotes the index in {aggregatorIndexRegion} to an aggregator
                index, unless another aggregator index exists
              </li>
              <li>
                Grants P0 permissions to list resources using the Resource
                Explorer API
              </li>
            </ul>
          </Typography.Paragraph>
          <Typography.Paragraph>
            To configure resource listing on this account, run these commands in{" "}
            <Typography.Link href={signinUrl} target="_blank" rel="noopener">
              AWS Cloud Shell
            </Typography.Link>
            :
          </Typography.Paragraph>
        </>
      ),
      commands: {
        shell: [
          ...Object.values(resourceExplorerCommands).map((commandBuilder) => ({
            command: commandBuilder(aggregatorIndexRegion),
          })),
          {
            command: inventoryInstallCommands(
              context,
              {},
              {
                gcloudServiceAccountId,
                awsAccountId: id,
                awsPartition,
              }
            ),
            iac: [
              {
                command: inventoryTerraform(
                  context,
                  {},
                  gcloudServiceAccountId,
                  awsPartition
                ),
              },
            ],
          },
        ],
      },
    };
  };

const inventoryOptionProvider: AwsInstaller["components"]["inventory"]["optionProvider"] =
  async ({ config }) => {
    const existingInventory = installedItems("inventory", config);
    return existingIamWriteAndAssessmentItems(config)
      .filter(([id]) => !(id in existingInventory))
      .map(([id, item]) => ({
        id,
        label: item?.label,
      }));
  };

export const awsInventoryInstaller: AwsInstaller["components"]["inventory"] = {
  instructions: inventoryInstructions,
  optionProvider: inventoryOptionProvider,
};
